import { useState, useCallback, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import {
    ShieldAlert, UploadCloud, FileText, Loader2, AlertTriangle,
    CheckCircle, Download, RefreshCw, ChevronDown, ChevronUp, ExternalLink,
    Clock, Trash2, History, DollarSign, TrendingDown, Globe,
} from 'lucide-react';
import Papa from 'papaparse';
import { uploadBatchCSV, getBatchStatus, getTariffOrigins, ClassificationResult, OriginRate } from '../services/api';
import toast from 'react-hot-toast';

// ── Types ─────────────────────────────────────────────────────────────────────

type ScanStatus = 'idle' | 'uploading' | 'processing' | 'done' | 'error';
type InputMode  = 'csv' | 'text';
type RiskLevel  = 'danger' | 'warning' | 'safe';

interface AltOption {
    hts_code: string;
    hts_code_formatted: string;
    description: string;
    cn_est_pct: number;
}

interface ScanItem {
    sku: string;
    product_description: string;
    hts_code: string;
    hts_code_formatted: string;
    hts_description: string;
    confidence: number;
    base_duty_pct: number;
    cn_total_pct: number;
    risk: RiskLevel;
    alts: AltOption[];
    error?: string;
}

interface ProfitCalc {
    unitCost:      number; // 进货成本 ($)
    tariffCost:    number; // 关税金额 ($)
    landedCost:    number; // 含税到岸成本 ($)
    requiredPrice: number; // 达到目标毛利率所需最低售价 ($)
}

// ── Scan session (localStorage persistence) ───────────────────────────────────

interface ScanSession {
    batchId:    string;
    label:      string;
    scannedAt:  string;
    totalItems: number;
    danger:     number;
    warning:    number;
    safe:       number;
    failed:     number;
    avgRate:    string;
}

const LS_KEY       = 'hsc_scan_history';
const MAX_SESSIONS = 10;

function loadSessions(): ScanSession[] {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '[]'); }
    catch { return []; }
}

function saveSession(session: ScanSession): void {
    const existing = loadSessions().filter((s) => s.batchId !== session.batchId);
    localStorage.setItem(LS_KEY, JSON.stringify([session, ...existing].slice(0, MAX_SESSIONS)));
}

function deleteSession(batchId: string): ScanSession[] {
    const updated = loadSessions().filter((s) => s.batchId !== batchId);
    localStorage.setItem(LS_KEY, JSON.stringify(updated));
    return updated;
}

function clearSessions(): void { localStorage.removeItem(LS_KEY); }

function formatRelativeTime(iso: string): string {
    const diff  = Date.now() - new Date(iso).getTime();
    const mins  = Math.floor(diff / 60_000);
    const hours = Math.floor(diff / 3_600_000);
    const days  = Math.floor(diff / 86_400_000);
    if (mins  <  1) return '刚刚';
    if (mins  < 60) return `${mins} 分钟前`;
    if (hours < 24) return `${hours} 小时前`;
    if (days  <  7) return `${days} 天前`;
    return new Date(iso).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
}

// ── Risk helpers ──────────────────────────────────────────────────────────────

function getRisk(cnTotalPct: number): RiskLevel {
    if (isNaN(cnTotalPct)) return 'safe';
    if (cnTotalPct >= 75)  return 'danger';
    if (cnTotalPct >= 20)  return 'warning';
    return 'safe';
}

function fmtRate(pct: number): string {
    if (isNaN(pct) || pct === null || pct === undefined) return '—';
    return `${pct}%`;
}

const RISK_LABEL: Record<RiskLevel, string> = { danger: '高风险', warning: '注意', safe: '较安全' };
const RISK_COLOR: Record<RiskLevel, string> = {
    danger:  'text-red-400',
    warning: 'text-amber-400',
    safe:    'text-emerald-400',
};
const RISK_BG: Record<RiskLevel, string> = {
    danger:  'bg-red-500/10 border-red-500/20',
    warning: 'bg-amber-500/10 border-amber-500/20',
    safe:    'bg-emerald-500/10 border-emerald-500/20',
};
const RISK_DOT: Record<RiskLevel, string> = {
    danger:  'bg-red-400',
    warning: 'bg-amber-400',
    safe:    'bg-emerald-400',
};

// ── Tariff estimation ─────────────────────────────────────────────────────────

function estimateCNTotal(htsCode: string, baseDutyRate: number | string | null | undefined): number {
    const rate = Number(baseDutyRate ?? 0);
    if (!htsCode || isNaN(rate)) return NaN;
    const code = htsCode.replace(/\./g, '');
    if (code.length < 2) return NaN;

    const chapter  = code.slice(0, 2);
    const heading4 = code.slice(0, 4);
    const exempted = ['8471','8473','8517','8528','8542','8543','8501','8504'];
    const isExempt = exempted.some((p) => heading4.startsWith(p));
    const s301_25  = new Set(['39','48','61','62','63','64','73','76','82','83','84','85','87','88','89','90','91','94','95']);
    const s301_75  = new Set(['22','36','40','44','45','46','59','66','68','69','70','71','93','96']);

    let additional = 0.20;
    if (!isExempt) additional += 1.25;
    if (s301_25.has(chapter)) additional += 0.25;
    else if (s301_75.has(chapter)) additional += 0.075;

    return parseFloat(((rate + additional) * 100).toFixed(1));
}

// ── Profit impact calculator ──────────────────────────────────────────────────
//
// Pure frontend calculation — tariff data already retrieved from API.
// Returns null when inputs are invalid (no cost entered, classification failed, etc.)
function getProfitCalc(
    item: ScanItem,
    unitCostStr: string | undefined,
    targetMarginPct: number,
): ProfitCalc | null {
    if (!unitCostStr) return null;
    const unitCost = parseFloat(unitCostStr);
    if (isNaN(unitCost) || unitCost <= 0) return null;
    if (item.error || isNaN(item.cn_total_pct)) return null;

    const tariffCost    = unitCost * (item.cn_total_pct / 100);
    const landedCost    = unitCost + tariffCost;
    const margin        = Math.max(0, Math.min(99.9, targetMarginPct));
    const requiredPrice = margin > 0 ? landedCost / (1 - margin / 100) : landedCost;

    return { unitCost, tariffCost, landedCost, requiredPrice };
}

// ── Transform API result → ScanItem ──────────────────────────────────────────

function toScanItem(raw: ClassificationResult & { sku?: string; product_description?: string; error?: string }): ScanItem {
    const dutyRate = Number(raw.duty_rate ?? 0);
    const isFailed = !!(raw.error || !raw.hts_code);
    const cnTotal  = isFailed ? NaN : estimateCNTotal(raw.hts_code, dutyRate);
    const risk     = getRisk(cnTotal);

    const alts: AltOption[] = isFailed ? [] : (raw.alternatives ?? [])
        .map((a) => ({
            hts_code:           a.hts_code,
            hts_code_formatted: a.hts_code.replace(/(\d{4})(\d{2})(\d{4})/, '$1.$2.$3'),
            description:        a.description,
            cn_est_pct:         estimateCNTotal(a.hts_code, 0),
        }))
        .filter((a) => !isNaN(a.cn_est_pct) && a.cn_est_pct < cnTotal)
        .sort((a, b) => a.cn_est_pct - b.cn_est_pct);

    return {
        sku:                 raw.sku || '—',
        product_description: raw.product_description || raw.hts_description || '（未知商品）',
        hts_code:            raw.hts_code || '',
        hts_code_formatted:  raw.hts_code_formatted || '',
        hts_description:     raw.hts_description || '',
        confidence:          Number(raw.confidence ?? 0),
        base_duty_pct:       isFailed ? NaN : parseFloat((dutyRate * 100).toFixed(1)),
        cn_total_pct:        cnTotal,
        risk,
        alts,
        error: raw.error,
    };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function RiskScanPage() {
    const [mode,            setMode]            = useState<InputMode>('csv');
    const [status,          setStatus]          = useState<ScanStatus>('idle');
    const [progress,        setProgress]        = useState(0);
    const [totalItems,      setTotalItems]      = useState(0);
    const [file,            setFile]            = useState<File | null>(null);
    const [quickText,       setQuickText]       = useState('');
    const [items,           setItems]           = useState<ScanItem[]>([]);
    const [expanded,        setExpanded]        = useState<Set<string>>(new Set());
    const [sessions,        setSessions]        = useState<ScanSession[]>([]);
    const [historyOpen,     setHistoryOpen]     = useState(false);
    const [loadingSession,  setLoadingSession]  = useState<string | null>(null);
    const [activeBatchId,   setActiveBatchId]   = useState<string | null>(null);
    const [activeLabel,     setActiveLabel]     = useState<string>('');
    const [activeScannedAt, setActiveScannedAt] = useState<string>('');

    // ── 利润影响分析状态 ───────────────────────────────────────────────────────
    const [profitMode,    setProfitMode]    = useState(false);
    const [globalCost,    setGlobalCost]    = useState('');       // 均摊进货成本 $
    const [targetMargin,  setTargetMargin]  = useState('30');     // 目标毛利率 %
    const [skuCosts,      setSkuCosts]      = useState<Record<string, string>>({}); // 各 SKU 成本覆盖

    // ── 多国货源对比状态 ────────────────────────────────────────────────────────
    // htsCode → OriginRate[] (lazy-loaded on expand)
    const [originData,    setOriginData]    = useState<Record<string, OriginRate[]>>({});
    const [originLoading, setOriginLoading] = useState<Record<string, boolean>>({});

    /** Fetch origin comparison for a given HTS code (lazy, cached). */
    const fetchOriginRates = async (htsCode: string) => {
        if (!htsCode || originData[htsCode] || originLoading[htsCode]) return;
        setOriginLoading((prev) => ({ ...prev, [htsCode]: true }));
        try {
            const result = await getTariffOrigins(htsCode);
            setOriginData((prev) => ({ ...prev, [htsCode]: result.origins }));
        } catch {
            // Silently fail — origin panel stays hidden
        } finally {
            setOriginLoading((prev) => ({ ...prev, [htsCode]: false }));
        }
    };

    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        const saved = loadSessions();
        setSessions(saved);
        if (saved.length > 0) setHistoryOpen(true);
    }, []);

    // ── Dropzone ──────────────────────────────────────────────────────────────

    const onDrop = useCallback((accepted: File[]) => {
        const f = accepted[0];
        if (!f) return;
        if (!f.name.endsWith('.csv')) { toast.error('请上传 CSV 文件（.csv）'); return; }
        setFile(f);
        setStatus('idle');
        setItems([]);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'text/csv': ['.csv'] },
        maxFiles: 1,
        maxSize: 10 * 1024 * 1024,
    });

    // ── Polling ───────────────────────────────────────────────────────────────

    const startPolling = (batchId: string, label: string) => {
        const startedAt = new Date().toISOString();
        pollRef.current = setInterval(async () => {
            try {
                const data = await getBatchStatus(batchId);
                setProgress(data.job.progress);
                setTotalItems(data.job.total_items);

                if (data.job.status === 'completed') {
                    clearInterval(pollRef.current!);
                    const scanned = (data.classifications as (ClassificationResult & { sku?: string; product_description?: string; error?: string })[])
                        .map(toScanItem)
                        .sort((a, b) => b.cn_total_pct - a.cn_total_pct);
                    setItems(scanned);
                    setActiveBatchId(batchId);
                    setActiveLabel(label);
                    setActiveScannedAt(startedAt);
                    setStatus('done');

                    const valid   = scanned.filter((i) => !i.error && !isNaN(i.cn_total_pct));
                    const failed  = scanned.filter((i) => !!i.error);
                    const danger  = valid.filter((i) => i.risk === 'danger');
                    const warning = valid.filter((i) => i.risk === 'warning');
                    const safe    = valid.filter((i) => i.risk === 'safe');
                    const avg     = valid.length
                        ? (valid.reduce((s, i) => s + i.cn_total_pct, 0) / valid.length).toFixed(1)
                        : '—';
                    saveSession({ batchId, label, scannedAt: startedAt, totalItems: scanned.length,
                        danger: danger.length, warning: warning.length, safe: safe.length,
                        failed: failed.length, avgRate: avg });
                    setSessions(loadSessions());
                    toast.success(`扫描完成！${scanned.length} 个产品已评估风险`);
                } else if (data.job.status === 'failed') {
                    clearInterval(pollRef.current!);
                    setStatus('error');
                    toast.error('扫描失败：' + data.job.error_message);
                }
            } catch { /* retry next tick */ }
        }, 1500);
    };

    // ── Load historical scan ──────────────────────────────────────────────────

    const loadSession = async (session: ScanSession) => {
        setLoadingSession(session.batchId);
        try {
            const data = await getBatchStatus(session.batchId);
            const scanned = (data.classifications as (ClassificationResult & { sku?: string; product_description?: string; error?: string })[])
                .map(toScanItem)
                .sort((a, b) => b.cn_total_pct - a.cn_total_pct);
            setItems(scanned);
            setActiveBatchId(session.batchId);
            setActiveLabel(session.label);
            setActiveScannedAt(session.scannedAt);
            setExpanded(new Set());
            setSkuCosts({}); // 历史记录加载时清空每 SKU 成本（原 CSV 成本不复存在）
            setStatus('done');
            setHistoryOpen(false);
        } catch {
            toast.error('无法加载历史记录，数据可能已过期');
        } finally {
            setLoadingSession(null);
        }
    };

    // ── Start scan ────────────────────────────────────────────────────────────

    const handleScan = async () => {
        let targetFile: File | null = null;

        if (mode === 'csv') {
            if (!file) { toast.error('请先上传 CSV 文件'); return; }
            targetFile = file;

            // 客户端解析 CSV 提取 unit_cost 列（纯前端，不发给后端）
            try {
                const text = await file.text();
                const parsed = Papa.parse<Record<string, string>>(text, { header: true, skipEmptyLines: true });
                const costMap: Record<string, string> = {};
                for (const row of parsed.data) {
                    const sku  = row.sku || row.SKU || row.id;
                    const cost = row.unit_cost || row.cost || row.cogs || row['Unit Cost'] || row['unit cost'];
                    if (sku && cost && parseFloat(cost) > 0) costMap[sku] = cost;
                }
                if (Object.keys(costMap).length > 0) {
                    setSkuCosts(costMap);
                    setProfitMode(true);
                    toast.success(`已读取 ${Object.keys(costMap).length} 个 SKU 的进货成本，利润分析已自动开启`);
                } else {
                    setSkuCosts({});
                }
            } catch { /* 解析失败时忽略，成本功能可选 */ }
        } else {
            const lines = quickText.trim().split('\n').filter((l) => l.trim().length > 3);
            if (lines.length === 0) { toast.error('请输入至少一条产品描述'); return; }
            if (lines.length > 50)  { toast.error('快速输入最多支持 50 个产品'); return; }
            const csv = `sku,description\n${lines
                .map((l, i) => `SCAN${String(i + 1).padStart(3, '0')},"${l.replace(/"/g, '""')}"`)
                .join('\n')}`;
            targetFile = new File([new Blob([csv], { type: 'text/csv' })], 'quick-scan.csv', { type: 'text/csv' });
            setSkuCosts({});
        }

        const label = mode === 'csv'
            ? (file?.name ?? 'scan.csv')
            : `快速输入 · ${quickText.trim().split('\n').filter((l) => l.trim().length > 3).length} 条`;

        setStatus('uploading');
        setItems([]);
        try {
            const data = await uploadBatchCSV(targetFile);
            setTotalItems(data.total_items);
            setStatus('processing');
            startPolling(data.batch_id, label);
            toast.success(`AI 正在扫描 ${data.total_items} 个产品的关税风险...`);
        } catch {
            setStatus('error');
            toast.error('上传失败，请检查文件格式后重试');
        }
    };

    // ── Helpers ───────────────────────────────────────────────────────────────

    const toggleExpand = (sku: string, htsCode?: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(sku)) {
                next.delete(sku);
            } else {
                next.add(sku);
                // Lazy-load origin rates when expanding a row
                if (htsCode) fetchOriginRates(htsCode);
            }
            return next;
        });
    };

    const handleResetSkuCosts = () => setSkuCosts({});

    // ── Derived stats ─────────────────────────────────────────────────────────

    const danger      = items.filter((i) => i.risk === 'danger');
    const warning     = items.filter((i) => i.risk === 'warning');
    const safe        = items.filter((i) => i.risk === 'safe');
    const validItems  = items.filter((i) => !i.error && !isNaN(i.cn_total_pct));
    const failedItems = items.filter((i) => i.error);
    const avgRate     = validItems.length
        ? (validItems.reduce((s, i) => s + i.cn_total_pct, 0) / validItems.length).toFixed(1)
        : '—';

    // 利润影响汇总（profitMode 下计算）
    const targetMarginNum = parseFloat(targetMargin) || 30;
    const profitCalcs = profitMode
        ? items.map((item) => {
            const costStr = skuCosts[item.sku] ?? (globalCost || undefined);
            return { item, calc: getProfitCalc(item, costStr, targetMarginNum) };
          })
        : [];
    const profitCoverage    = profitCalcs.filter((p) => p.calc !== null).length;
    const totalTariffCost   = profitCalcs.reduce((s, p) => s + (p.calc?.tariffCost ?? 0), 0);
    const avgLandedCost     = profitCoverage > 0
        ? profitCalcs.filter((p) => p.calc).reduce((s, p) => s + p.calc!.landedCost, 0) / profitCoverage
        : 0;
    const avgRequiredPrice  = profitCoverage > 0
        ? profitCalcs.filter((p) => p.calc).reduce((s, p) => s + p.calc!.requiredPrice, 0) / profitCoverage
        : 0;

    // ── Export CSV ────────────────────────────────────────────────────────────

    const exportCSV = () => {
        if (!items.length) return;

        const profitActive = profitMode && profitCoverage > 0;
        const meta = [
            { 风险等级: `// 税码通 关税风险扫描报告`, SKU: '', 产品描述: '', HTS编码: '', 基础税率: '', 对华综合税率: '', 建议备选编码: '', 备选税率下限: '', ...(profitActive ? { 进货成本_USD: '', 含税到岸成本: '', [`目标售价_${targetMargin}%毛利`]: '' } : {}) },
            { 风险等级: `// 生成时间: ${new Date().toLocaleString('zh-CN')}`, SKU: '', 产品描述: '', HTS编码: '', 基础税率: '', 对华综合税率: '', 建议备选编码: '', 备选税率下限: '', ...(profitActive ? { 进货成本_USD: '', 含税到岸成本: '', [`目标售价_${targetMargin}%毛利`]: '' } : {}) },
            { 风险等级: `// 扫描总数: ${items.length}  高风险: ${danger.length}  需注意: ${warning.length}  较安全: ${safe.length}  平均对华税率: ${avgRate}%${profitActive ? `  总税负: $${totalTariffCost.toFixed(2)}  均含税成本: $${avgLandedCost.toFixed(2)}` : ''}`, SKU: '', 产品描述: '', HTS编码: '', 基础税率: '', 对华综合税率: '', 建议备选编码: '', 备选税率下限: '', ...(profitActive ? { 进货成本_USD: '', 含税到岸成本: '', [`目标售价_${targetMargin}%毛利`]: '' } : {}) },
            { 风险等级: `// 对华综合税率估算含Section301+2025行政令，仅供参考`, SKU: '', 产品描述: '', HTS编码: '', 基础税率: '', 对华综合税率: '', 建议备选编码: '', 备选税率下限: '', ...(profitActive ? { 进货成本_USD: '', 含税到岸成本: '', [`目标售价_${targetMargin}%毛利`]: '' } : {}) },
        ];

        const rows = [...danger, ...warning, ...safe].map((item) => {
            const base = {
                风险等级:     RISK_LABEL[item.risk],
                SKU:          item.sku,
                产品描述:     item.product_description,
                HTS编码:      item.hts_code_formatted,
                基础税率:     fmtRate(item.base_duty_pct),
                对华综合税率: fmtRate(item.cn_total_pct),
                建议备选编码: item.alts[0]?.hts_code_formatted || '—',
                备选税率下限: item.alts[0] ? `${item.alts[0].cn_est_pct}%起` : '—',
            };
            if (profitActive) {
                const costStr = skuCosts[item.sku] ?? (globalCost || undefined);
                const calc    = getProfitCalc(item, costStr, targetMarginNum);
                return {
                    ...base,
                    进货成本_USD:                     calc ? `$${calc.unitCost.toFixed(2)}`      : '—',
                    含税到岸成本:                     calc ? `$${calc.landedCost.toFixed(2)}`    : '—',
                    [`目标售价_${targetMargin}%毛利`]: calc ? `$${calc.requiredPrice.toFixed(2)}` : '—',
                };
            }
            return base;
        });

        const csv = Papa.unparse([...meta, ...rows]);
        const url = URL.createObjectURL(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }));
        const a   = document.createElement('a');
        a.href = url;
        a.download = `税码通_关税风险报告_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // ── Export PDF ────────────────────────────────────────────────────────────

    const exportPDF = () => {
        if (!items.length) return;

        const now         = new Date().toLocaleString('zh-CN');
        const dateSlug    = new Date().toISOString().slice(0, 10);
        const sortedItems = [...danger, ...warning, ...safe];
        const dangerPct   = items.length ? Math.round(danger.length  / items.length * 100) : 0;
        const warningPct  = items.length ? Math.round(warning.length / items.length * 100) : 0;
        const safePct     = 100 - dangerPct - warningPct;
        const profitActive = profitMode && profitCoverage > 0;

        const profitSummaryHTML = profitActive ? `
        <div class="profit-summary">
          <span class="ps-label">💰 利润影响分析</span>
          <span>已填 <b>${profitCoverage}</b>/${items.length} 个 SKU</span>
          <span>预计总税负 <b style="color:#ef4444">$${totalTariffCost.toFixed(2)}</b></span>
          <span>均含税成本 <b>$${avgLandedCost.toFixed(2)}/件</b></span>
          <span>按 ${targetMargin}% 毛利均需售价 <b style="color:#8b5cf6">$${avgRequiredPrice.toFixed(2)}+</b></span>
        </div>` : '';

        const rowsHTML = sortedItems.map((item) => {
            const riskColor = item.risk === 'danger' ? '#ef4444' : item.risk === 'warning' ? '#f59e0b' : '#10b981';
            const altText   = item.alts[0]
                ? `${item.alts[0].hts_code_formatted}（↓${item.alts[0].cn_est_pct}%）`
                : '—';
            const profitCells = profitActive ? (() => {
                const costStr = skuCosts[item.sku] ?? (globalCost || undefined);
                const calc    = getProfitCalc(item, costStr, targetMarginNum);
                if (!calc) return `<td class="num">—</td><td class="num">—</td>`;
                return `<td class="num">$${calc.landedCost.toFixed(2)}</td><td class="num" style="color:#8b5cf6;font-weight:700">$${calc.requiredPrice.toFixed(2)}</td>`;
            })() : '';
            return `
            <tr class="${item.risk}-row">
                <td style="color:${riskColor};font-weight:700">${RISK_LABEL[item.risk]}</td>
                <td class="mono">${item.sku}</td>
                <td class="desc">${item.product_description}</td>
                <td class="mono">${item.hts_code_formatted}</td>
                <td class="num">${fmtRate(item.base_duty_pct)}</td>
                <td class="num" style="color:${riskColor};font-weight:700">${fmtRate(item.cn_total_pct)}</td>
                <td class="alt">${altText}</td>
                ${profitCells}
            </tr>`;
        }).join('');

        const profitHeaderCells = profitActive
            ? `<th style="text-align:right">含税到岸成本</th><th style="text-align:right">目标售价(${targetMargin}%)</th>`
            : '';

        const html = `<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="utf-8">
<title>税码通_关税风险报告_${dateSlug}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:-apple-system,'PingFang SC','Microsoft YaHei',sans-serif;font-size:12px;color:#1e293b;background:#fff}
  .header{background:#0f172a;color:#fff;padding:14px 22px;display:flex;justify-content:space-between;align-items:flex-start}
  .header h1{font-size:17px;font-weight:700}
  .header .sub{font-size:10px;color:#94a3b8;margin-top:3px}
  .header .meta{font-size:10px;color:#94a3b8;text-align:right;line-height:1.8}
  .summary{display:flex;gap:10px;padding:12px 22px;background:#f8fafc;border-bottom:1px solid #e2e8f0}
  .stat{flex:1;text-align:center;padding:10px 6px;border-radius:7px;background:#fff;border:1px solid #e2e8f0}
  .stat .val{font-size:22px;font-weight:700}
  .stat .lbl{font-size:10px;color:#64748b;margin-top:2px}
  .profit-summary{display:flex;flex-wrap:wrap;gap:16px;padding:8px 22px;background:#f5f3ff;border-bottom:1px solid #ddd6fe;font-size:10px;color:#4c1d95}
  .ps-label{font-weight:700;color:#6d28d9}
  .dist{padding:8px 22px;display:flex;align-items:center;gap:10px;font-size:10px;color:#64748b;border-bottom:1px solid #e2e8f0}
  .bar{flex:1;height:7px;border-radius:4px;overflow:hidden;display:flex;background:#f1f5f9}
  .bar-d{background:#ef4444}.bar-w{background:#f59e0b}.bar-s{background:#10b981}
  .legend{display:flex;gap:14px}
  .content{padding:12px 22px}
  table{width:100%;border-collapse:collapse;font-size:11px}
  th{background:#1e293b;color:#fff;padding:7px 9px;text-align:left;font-size:10px;font-weight:600;white-space:nowrap}
  td{padding:6px 9px;border-bottom:1px solid #f1f5f9;vertical-align:middle}
  tr:nth-child(even) td{background:#f8fafc}
  .danger-row  td:first-child{border-left:3px solid #ef4444}
  .warning-row td:first-child{border-left:3px solid #f59e0b}
  .safe-row    td:first-child{border-left:3px solid #10b981}
  .mono{font-family:'Courier New',monospace;font-size:10px}
  .desc{max-width:160px;word-break:break-word;font-size:10px}
  .num{text-align:right;white-space:nowrap}
  .alt{font-size:10px;color:#059669}
  .disclaimer{margin:10px 22px;padding:9px 12px;background:#fefce8;border:1px solid #fde68a;border-radius:5px;font-size:9px;color:#92400e;line-height:1.6}
  .footer{padding:8px 22px;font-size:9px;color:#94a3b8;border-top:1px solid #f1f5f9;display:flex;justify-content:space-between;margin-top:4px}
  @media print{@page{margin:.8cm;size:A4 landscape}body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style>
</head>
<body>
<div class="header">
  <div>
    <h1>税码通 — 关税风险扫描报告</h1>
    <div class="sub">对华综合税率估算（含 Section 301 + 2025 行政令 + 芬太尼 EO）</div>
  </div>
  <div class="meta">
    <div>生成时间：${now}</div>
    <div>扫描产品数：${items.length} 个 SKU</div>
  </div>
</div>
<div class="summary">
  <div class="stat"><div class="val" style="color:#1e293b">${items.length}</div><div class="lbl">扫描总数</div></div>
  <div class="stat"><div class="val" style="color:#ef4444">${danger.length}</div><div class="lbl">高风险 ≥75%</div></div>
  <div class="stat"><div class="val" style="color:#f59e0b">${warning.length}</div><div class="lbl">需注意 20–75%</div></div>
  <div class="stat"><div class="val" style="color:#10b981">${safe.length}</div><div class="lbl">较安全 &lt;20%</div></div>
  <div class="stat"><div class="val" style="color:#6366f1">${avgRate}%</div><div class="lbl">平均对华税率</div></div>
  ${profitActive ? `<div class="stat"><div class="val" style="color:#ef4444">$${totalTariffCost.toFixed(0)}</div><div class="lbl">预计总税负</div></div>` : ''}
  ${profitActive ? `<div class="stat"><div class="val" style="color:#8b5cf6">$${avgRequiredPrice.toFixed(2)}</div><div class="lbl">均需售价(${targetMargin}%)</div></div>` : ''}
</div>
${profitSummaryHTML}
<div class="dist">
  <span>风险分布</span>
  <div class="bar">
    ${dangerPct  > 0 ? `<div class="bar-d" style="width:${dangerPct}%"></div>`  : ''}
    ${warningPct > 0 ? `<div class="bar-w" style="width:${warningPct}%"></div>` : ''}
    ${safePct    > 0 ? `<div class="bar-s" style="width:${safePct}%"></div>`    : ''}
  </div>
  <div class="legend">
    <span style="color:#ef4444">■ 高风险 ${dangerPct}%</span>
    <span style="color:#f59e0b">■ 需注意 ${warningPct}%</span>
    <span style="color:#10b981">■ 较安全 ${safePct}%</span>
  </div>
</div>
<div class="content">
  <table>
    <thead>
      <tr>
        <th>风险等级</th><th>SKU</th><th>产品描述</th>
        <th>HTS 编码</th><th style="text-align:right">基础税率</th>
        <th style="text-align:right">对华综合税率</th><th>备选优化建议</th>
        ${profitHeaderCells}
      </tr>
    </thead>
    <tbody>${rowsHTML}</tbody>
  </table>
</div>
<div class="disclaimer">
  ⚠ 对华综合税率为估算值，含 2025 年行政令（145% 对华综合关税）、Section 301 附加税及芬太尼行政令。消费电子等品类暂时豁免 125% 对等税。
  ${profitActive ? `利润影响数据基于用户填写的进货成本（$${globalCost || '各SKU填写值'}/件）及 ${targetMargin}% 目标毛利率估算。` : ''}
  所有结果仅供参考，正式报关请咨询持牌报关行确认编码适用性。
</div>
<div class="footer">
  <span>© ${new Date().getFullYear()} 税码通 · AI HTS 编码分类平台</span>
  <span>本报告由 AI 生成，税率数据以海关核定为准</span>
</div>
<script>window.onload=()=>{document.title='税码通_关税风险报告_${dateSlug}';window.print()}<\/script>
</body></html>`;

        const win = window.open('', '_blank', 'width=1200,height=800');
        if (!win) { toast.error('请在浏览器中允许弹出窗口，然后重试'); return; }
        win.document.write(html);
        win.document.close();
    };

    // ── Template ──────────────────────────────────────────────────────────────

    const downloadTemplate = () => {
        // 模板包含可选的 unit_cost 列，若填写则自动开启利润分析
        const csv = `sku,description,unit_cost\nSKU001,Wireless Bluetooth headphones over-ear noise cancelling,12.50\nSKU002,Women leather crossbody bag gold hardware,8.00\nSKU003,Stainless steel water bottle 32oz insulated,5.50`;
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
        const a   = document.createElement('a');
        a.href = url; a.download = '税码通_扫描模板.csv'; a.click();
        URL.revokeObjectURL(url);
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="p-6 max-w-6xl mx-auto animate-fade-in">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center gap-2 mb-2">
                    <ShieldAlert size={20} className="text-red-400" />
                    <h1 className="text-xl font-bold text-white">选品关税风险扫描</h1>
                    <span className="badge badge-red ml-1">2025 年 145% 对华关税</span>
                </div>
                <p className="text-slate-400 text-sm">
                    上传商品清单，AI 一键评估关税风险，发现高危品类并推荐备选编码。CSV 可含 <span className="font-mono text-slate-300">unit_cost</span> 列自动开启利润分析。
                </p>
            </div>

            {/* 历史扫描面板 */}
            {sessions.length > 0 && (
                <div className="mb-5">
                    <button
                        onClick={() => setHistoryOpen((v) => !v)}
                        className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-2 group"
                    >
                        <History size={15} className="text-brand-400" />
                        <span className="font-medium">最近扫描记录</span>
                        <span className="text-xs text-slate-600 bg-surface-2 px-1.5 py-0.5 rounded-full">{sessions.length}</span>
                        {historyOpen
                            ? <ChevronUp size={14} className="ml-auto text-slate-600 group-hover:text-slate-400" />
                            : <ChevronDown size={14} className="ml-auto text-slate-600 group-hover:text-slate-400" />
                        }
                    </button>

                    {historyOpen && (
                        <div className="card p-0 overflow-hidden animate-fade-in">
                            <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06] bg-surface-2/40">
                                <span className="text-xs text-slate-500">点击任意记录可重新加载结果</span>
                                <button
                                    onClick={() => { clearSessions(); setSessions([]); setHistoryOpen(false); }}
                                    className="flex items-center gap-1 text-xs text-slate-600 hover:text-red-400 transition-colors"
                                >
                                    <Trash2 size={11} /> 清除全部
                                </button>
                            </div>
                            <div className="divide-y divide-white/[0.04]">
                                {sessions.map((s) => {
                                    const isActive  = s.batchId === activeBatchId;
                                    const isLoading = loadingSession === s.batchId;
                                    const total     = s.danger + s.warning + s.safe;
                                    return (
                                        <div
                                            key={s.batchId}
                                            onClick={() => !isLoading && loadSession(s)}
                                            className={`flex items-center gap-4 px-4 py-3 cursor-pointer transition-colors group
                                                ${isActive
                                                    ? 'bg-brand-500/8 border-l-2 border-l-brand-500'
                                                    : 'hover:bg-white/[0.02] border-l-2 border-l-transparent'
                                                }`}
                                        >
                                            {total > 0 ? (
                                                <div className="w-1 h-8 rounded-full overflow-hidden flex flex-col flex-shrink-0">
                                                    {s.danger  > 0 && <div className="bg-red-500"     style={{ flex: s.danger }} />}
                                                    {s.warning > 0 && <div className="bg-amber-500"   style={{ flex: s.warning }} />}
                                                    {s.safe    > 0 && <div className="bg-emerald-500" style={{ flex: s.safe }} />}
                                                </div>
                                            ) : (
                                                <div className="w-1 h-8 rounded-full bg-slate-700 flex-shrink-0" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <p className="text-sm text-slate-200 truncate font-medium">{s.label}</p>
                                                    {isActive && (
                                                        <span className="text-[10px] text-brand-400 bg-brand-500/15 px-1.5 py-0.5 rounded-full flex-shrink-0">当前</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 mt-0.5 text-[10px] text-slate-600">
                                                    <span className="flex items-center gap-1"><Clock size={9} />{formatRelativeTime(s.scannedAt)}</span>
                                                    <span>{s.totalItems} 个 SKU</span>
                                                    {s.avgRate !== '—' && <span>均 {s.avgRate}%</span>}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs flex-shrink-0">
                                                {s.danger  > 0 && <span className="text-red-400 font-semibold">🔴 {s.danger}</span>}
                                                {s.warning > 0 && <span className="text-amber-400 font-semibold">🟡 {s.warning}</span>}
                                                {s.safe    > 0 && <span className="text-emerald-400">🟢 {s.safe}</span>}
                                                {s.failed  > 0 && <span className="text-slate-600">⚫ {s.failed}</span>}
                                            </div>
                                            <div className="flex-shrink-0 w-5 text-right">
                                                {isLoading
                                                    ? <Loader2 size={14} className="text-brand-400 animate-spin" />
                                                    : <span className="text-slate-700 group-hover:text-brand-400 transition-colors text-sm">›</span>
                                                }
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const updated = deleteSession(s.batchId);
                                                    setSessions(updated);
                                                    if (activeBatchId === s.batchId) {
                                                        setStatus('idle'); setItems([]); setActiveBatchId(null);
                                                    }
                                                }}
                                                className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-slate-600 hover:text-red-400"
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Input + status */}
            {status !== 'done' && (
                <div className="card mb-6">
                    <div className="flex gap-1 mb-5 bg-surface-2 rounded-xl p-1 w-fit">
                        {(['csv', 'text'] as InputMode[]).map((m) => (
                            <button
                                key={m}
                                onClick={() => setMode(m)}
                                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                    mode === m ? 'bg-brand-600 text-white shadow' : 'text-slate-400 hover:text-white'
                                }`}
                            >
                                {m === 'csv' ? '📄 上传 CSV' : '✏️ 快速输入'}
                            </button>
                        ))}
                    </div>

                    {mode === 'csv' && (
                        <div
                            {...getRootProps()}
                            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all mb-4 ${
                                isDragActive
                                    ? 'border-brand-500 bg-brand-500/10'
                                    : file
                                    ? 'border-emerald-500/40 bg-emerald-500/5'
                                    : 'border-white/10 hover:border-brand-500/40 hover:bg-brand-500/5'
                            }`}
                        >
                            <input {...getInputProps()} />
                            {file ? (
                                <div className="flex flex-col items-center gap-2">
                                    <CheckCircle size={32} className="text-emerald-400" />
                                    <p className="text-white font-medium">{file.name}</p>
                                    <p className="text-slate-500 text-sm">{(file.size / 1024).toFixed(1)} KB · 点击替换</p>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2">
                                    <UploadCloud size={32} className="text-slate-600" />
                                    <p className="text-white font-medium">
                                        {isDragActive ? '释放以上传 CSV 文件' : '拖拽 CSV 文件至此'}
                                    </p>
                                    <p className="text-slate-500 text-sm">或点击选择 · 最多 1,000 行 · 仅限 .csv</p>
                                    <p className="text-xs text-slate-600 mt-1">
                                        格式：<span className="font-mono text-slate-500">sku,description[,unit_cost]</span>
                                        <span className="text-slate-600 ml-1">· 含 unit_cost 列可自动开启利润分析</span>
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {mode === 'text' && (
                        <div className="mb-4">
                            <label className="text-xs text-slate-400 mb-1.5 block">
                                每行输入一条产品描述（建议用英文，最多 50 条）
                            </label>
                            <textarea
                                value={quickText}
                                onChange={(e) => setQuickText(e.target.value)}
                                placeholder={`Wireless Bluetooth headphones with ANC\nWomen's leather crossbody bag gold hardware\nStainless steel water bottle 32oz insulated\n...`}
                                rows={8}
                                className="input-field resize-y text-sm font-mono leading-relaxed"
                            />
                            <p className="text-xs text-slate-600 mt-1">
                                已输入 {quickText.trim().split('\n').filter((l) => l.trim().length > 3).length} 条 / 最多 50 条
                            </p>
                        </div>
                    )}

                    <div className="flex items-center justify-between">
                        {mode === 'csv' && (
                            <button onClick={downloadTemplate} className="btn-secondary text-sm gap-1.5">
                                <FileText size={14} />
                                下载模板（含利润列）
                            </button>
                        )}
                        <div className={mode === 'text' ? 'ml-auto' : ''}>
                            <button
                                onClick={handleScan}
                                disabled={status === 'uploading' || status === 'processing'}
                                className="btn-primary gap-2"
                            >
                                {(status === 'uploading' || status === 'processing') ? (
                                    <><Loader2 size={16} className="animate-spin" /> 扫描中...</>
                                ) : (
                                    <><ShieldAlert size={16} /> 开始风险扫描</>
                                )}
                            </button>
                        </div>
                    </div>

                    {status === 'processing' && (
                        <div className="mt-5 animate-fade-in">
                            <div className="flex items-center gap-3 mb-3">
                                <Loader2 size={15} className="text-brand-400 animate-spin flex-shrink-0" />
                                <p className="text-sm text-slate-300">
                                    AI 正在评估关税风险... {Math.round(progress * totalItems / 100)} / {totalItems}
                                </p>
                                <span className="ml-auto text-xs font-mono text-slate-500">{progress}%</span>
                            </div>
                            <div className="h-2 bg-surface-2 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-red-500 via-amber-500 to-emerald-500 rounded-full transition-all duration-500"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="mt-4 flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-xl p-3 animate-fade-in">
                            <AlertTriangle size={16} className="text-red-400 flex-shrink-0" />
                            <p className="text-sm text-red-300">扫描失败，请检查文件格式后重试</p>
                            <button onClick={() => setStatus('idle')} className="btn-secondary ml-auto text-sm">重试</button>
                        </div>
                    )}
                </div>
            )}

            {/* Results */}
            {status === 'done' && items.length > 0 && (
                <div className="space-y-5 animate-slide-up">
                    {/* Summary cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                        {[
                            { label: '扫描总数',     value: items.length,    sub: '个产品',       color: 'text-white' },
                            { label: '高风险',       value: danger.length,   sub: '≥ 75% 综合税', color: 'text-red-400' },
                            { label: '需注意',       value: warning.length,  sub: '20–75%',       color: 'text-amber-400' },
                            { label: '较安全',       value: safe.length,     sub: '< 20%',        color: 'text-emerald-400' },
                            { label: '平均对华税率', value: `${avgRate}%`,   sub: '综合估算',     color: 'text-brand-400' },
                        ].map((s) => (
                            <div key={s.label} className="card text-center py-4">
                                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                                <p className="text-xs font-medium text-white mt-0.5">{s.label}</p>
                                <p className="text-[10px] text-slate-600 mt-0.5">{s.sub}</p>
                            </div>
                        ))}
                    </div>

                    {/* 利润影响汇总（有数据时显示） */}
                    {profitMode && profitCoverage > 0 && (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-fade-in">
                            {[
                                { label: '预计总税负',   value: `$${totalTariffCost.toFixed(2)}`,    sub: `${profitCoverage}/${items.length} SKU 已填成本`, color: 'text-red-400' },
                                { label: '均含税到岸成本', value: `$${avgLandedCost.toFixed(2)}`,   sub: '含关税 / 件',    color: 'text-orange-400' },
                                { label: '均目标售价',   value: `$${avgRequiredPrice.toFixed(2)}+`,  sub: `保 ${targetMargin}% 毛利`, color: 'text-violet-400' },
                                { label: '税率倍增',     value: `${validItems.length > 0 && profitCoverage > 0 ? ((totalTariffCost / profitCalcs.filter(p=>p.calc).reduce((s,p)=>s+p.calc!.unitCost,0)*100)||0).toFixed(0) : '—'}%`,  sub: '税负占进货成本比', color: 'text-slate-300' },
                            ].map((s) => (
                                <div key={s.label} className="card text-center py-3 border border-violet-500/20 bg-violet-500/5">
                                    <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                                    <p className="text-xs font-medium text-white mt-0.5">{s.label}</p>
                                    <p className="text-[10px] text-slate-600 mt-0.5">{s.sub}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Failed items notice */}
                    {failedItems.length > 0 && (
                        <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-500/10 border border-slate-500/20 rounded-xl text-xs text-slate-400">
                            <AlertTriangle size={13} className="text-slate-500 flex-shrink-0" />
                            <span>
                                {failedItems.length} 个产品 AI 分类失败，税率无法估算，已在结果中标记为「—」。
                                建议检查这些商品的描述是否足够详细后重新扫描。
                            </span>
                        </div>
                    )}

                    {/* Risk distribution bar */}
                    <div className="card py-3">
                        <div className="flex items-center gap-3 mb-2">
                            <p className="text-xs font-semibold text-slate-400">风险分布</p>
                            <div className="flex-1 flex gap-1 h-3 rounded-full overflow-hidden">
                                {danger.length  > 0 && <div className="bg-red-500"     style={{ width: `${danger.length  / items.length * 100}%` }} />}
                                {warning.length > 0 && <div className="bg-amber-500"   style={{ width: `${warning.length / items.length * 100}%` }} />}
                                {safe.length    > 0 && <div className="bg-emerald-500" style={{ width: `${safe.length    / items.length * 100}%` }} />}
                            </div>
                        </div>
                        <div className="flex gap-4 text-[10px] text-slate-500">
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> 高风险 {Math.round(danger.length  / items.length * 100)}%</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> 需注意 {Math.round(warning.length / items.length * 100)}%</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> 较安全 {Math.round(safe.length / items.length * 100)}%</span>
                        </div>
                    </div>

                    {/* ── 利润分析控制面板 ── */}
                    <div className={`card transition-all ${profitMode ? 'border-violet-500/25 bg-violet-500/5' : 'border-white/[0.06]'}`}>
                        <div className="flex items-center gap-4 flex-wrap">
                            {/* 开关 */}
                            <button
                                onClick={() => setProfitMode((v) => !v)}
                                className={`flex items-center gap-2 text-sm font-semibold transition-colors ${
                                    profitMode ? 'text-violet-400' : 'text-slate-400 hover:text-white'
                                }`}
                            >
                                <DollarSign size={15} className={profitMode ? 'text-violet-400' : 'text-slate-500'} />
                                利润影响分析
                                <div className={`w-8 h-4 rounded-full transition-colors relative flex-shrink-0 ${profitMode ? 'bg-violet-600' : 'bg-slate-700'}`}>
                                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${profitMode ? 'left-4' : 'left-0.5'}`} />
                                </div>
                            </button>

                            {profitMode && (
                                <>
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="text-slate-400 text-xs whitespace-nowrap">均摊进货成本</span>
                                        <div className="flex items-center border border-white/[0.08] bg-surface-2 rounded-lg overflow-hidden">
                                            <span className="text-slate-500 text-xs pl-2">$</span>
                                            <input
                                                type="number" min="0" step="0.01"
                                                value={globalCost}
                                                onChange={(e) => setGlobalCost(e.target.value)}
                                                placeholder="12.50"
                                                className="w-20 bg-transparent px-2 py-1.5 text-xs text-white text-right focus:outline-none"
                                            />
                                            <span className="text-slate-500 text-xs pr-2">/件</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="text-slate-400 text-xs whitespace-nowrap">目标毛利率</span>
                                        <div className="flex items-center border border-white/[0.08] bg-surface-2 rounded-lg overflow-hidden">
                                            <input
                                                type="number" min="1" max="99" step="1"
                                                value={targetMargin}
                                                onChange={(e) => setTargetMargin(e.target.value)}
                                                className="w-14 bg-transparent px-2 py-1.5 text-xs text-white text-right focus:outline-none"
                                            />
                                            <span className="text-slate-500 text-xs pr-2">%</span>
                                        </div>
                                    </div>

                                    {Object.keys(skuCosts).length > 0 && (
                                        <button
                                            onClick={handleResetSkuCosts}
                                            className="text-xs text-slate-600 hover:text-violet-400 transition-colors"
                                        >
                                            重置各行为均摊值
                                        </button>
                                    )}

                                    <div className="ml-auto text-xs text-slate-600 hidden lg:block">
                                        💡 CSV 含 <span className="font-mono text-slate-500">unit_cost</span> 列可自动读取各 SKU 成本
                                    </div>
                                </>
                            )}

                            {!profitMode && (
                                <p className="text-xs text-slate-600">
                                    开启后可输入进货成本，估算各 SKU 含税到岸成本与目标售价
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Action bar */}
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <p className="text-sm font-semibold text-white">
                                风险清单 <span className="text-slate-500 font-normal">— 按风险由高到低排序</span>
                            </p>
                            {activeLabel && (
                                <div className="flex items-center gap-1.5 mt-0.5 text-xs text-slate-500">
                                    <Clock size={10} />
                                    <span className="truncate max-w-xs">{activeLabel}</span>
                                    {activeScannedAt && (
                                        <span className="text-slate-600">· {formatRelativeTime(activeScannedAt)}</span>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => { setStatus('idle'); setItems([]); setFile(null); setQuickText(''); setActiveBatchId(null); setSkuCosts({}); setHistoryOpen(sessions.length > 0); }}
                                className="btn-secondary text-sm gap-1.5"
                            >
                                <RefreshCw size={14} />
                                新建扫描
                            </button>
                            <button onClick={exportCSV} className="btn-secondary text-sm gap-1.5">
                                <Download size={14} />
                                导出 CSV
                            </button>
                            <button onClick={exportPDF} className="btn-secondary text-sm gap-1.5">
                                <FileText size={14} />
                                导出 PDF
                            </button>
                        </div>
                    </div>

                    {/* Risk table */}
                    <div className="card p-0 overflow-hidden">
                        <div className={profitMode ? 'overflow-x-auto' : ''}>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-white/[0.06]">
                                        {['风险', 'SKU', '产品描述', 'HTS 编码', '基础税率', '对华综合税率', '备选方案'].map((h) => (
                                            <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                                                {h}
                                            </th>
                                        ))}
                                        {profitMode && (
                                            <>
                                                <th className="px-3 py-3 text-left text-xs font-semibold text-violet-400 uppercase tracking-wide whitespace-nowrap">
                                                    进货成本 ($)
                                                </th>
                                                <th className="px-3 py-3 text-left text-xs font-semibold text-violet-400 uppercase tracking-wide whitespace-nowrap">
                                                    含税到岸 → 目标售价
                                                </th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item) => {
                                        const isOpen    = expanded.has(item.sku);
                                        const costStr   = skuCosts[item.sku] ?? globalCost;
                                        const profitRes = profitMode ? getProfitCalc(item, costStr || undefined, targetMarginNum) : null;

                                        return (
                                            <>
                                                <tr
                                                    key={item.sku}
                                                    className={`border-b border-white/[0.04] transition-colors hover:bg-white/[0.02] ${
                                                        item.risk === 'danger'  ? 'border-l-2 border-l-red-500/60' :
                                                        item.risk === 'warning' ? 'border-l-2 border-l-amber-500/60' :
                                                                                  'border-l-2 border-l-emerald-500/40'
                                                    }`}
                                                >
                                                    {/* Risk badge */}
                                                    <td className="px-4 py-3">
                                                        {item.error ? (
                                                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                                                                <span className="w-2 h-2 rounded-full flex-shrink-0 bg-slate-600" />
                                                                分类失败
                                                            </span>
                                                        ) : (
                                                            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${RISK_COLOR[item.risk]}`}>
                                                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${RISK_DOT[item.risk]}`} />
                                                                {RISK_LABEL[item.risk]}
                                                            </span>
                                                        )}
                                                    </td>
                                                    {/* SKU */}
                                                    <td className="px-4 py-3 font-mono text-brand-300 text-xs whitespace-nowrap">{item.sku}</td>
                                                    {/* Description */}
                                                    <td className="px-4 py-3 text-slate-300 max-w-xs">
                                                        <p className="line-clamp-1 text-xs">{item.product_description}</p>
                                                    </td>
                                                    {/* HTS code */}
                                                    <td className="px-4 py-3">
                                                        {item.error || !item.hts_code_formatted ? (
                                                            <span className="text-xs text-slate-600 italic">—</span>
                                                        ) : (
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="hts-code">{item.hts_code_formatted}</span>
                                                                <a
                                                                    href={`https://hts.usitc.gov/?query=${item.hts_code}`}
                                                                    target="_blank" rel="noreferrer"
                                                                    className="text-slate-600 hover:text-brand-400 transition-colors"
                                                                >
                                                                    <ExternalLink size={11} />
                                                                </a>
                                                            </div>
                                                        )}
                                                    </td>
                                                    {/* Base duty */}
                                                    <td className="px-4 py-3 text-slate-400 text-xs font-mono">{fmtRate(item.base_duty_pct)}</td>
                                                    {/* CN total */}
                                                    <td className="px-4 py-3">
                                                        {item.error ? (
                                                            <span className="text-xs text-slate-500 italic" title="AI 未能完成分类，无法估算税率">—</span>
                                                        ) : (
                                                            <span className={`text-sm font-bold ${RISK_COLOR[item.risk]}`}>
                                                                {fmtRate(item.cn_total_pct)}
                                                            </span>
                                                        )}
                                                    </td>
                                                    {/* Alternatives + origin expand */}
                                                    <td className="px-4 py-3">
                                                        <div className="flex flex-col gap-1">
                                                            {item.alts.length > 0 ? (
                                                                <button
                                                                    onClick={() => toggleExpand(item.sku, item.hts_code)}
                                                                    className="flex items-center gap-1 text-xs text-brand-400 hover:text-brand-300 transition-colors"
                                                                >
                                                                    <span className="font-mono">{item.alts[0].hts_code_formatted}</span>
                                                                    <span className="text-emerald-400">↓{fmtRate(item.alts[0].cn_est_pct)}</span>
                                                                    {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                                                </button>
                                                            ) : (
                                                                <span className="text-xs text-slate-600">—</span>
                                                            )}
                                                            {/* 多国对比按钮 */}
                                                            {!item.error && item.hts_code && (
                                                                <button
                                                                    onClick={() => toggleExpand(item.sku, item.hts_code)}
                                                                    className={`flex items-center gap-1 text-[10px] transition-colors ${
                                                                        isOpen
                                                                            ? 'text-violet-400'
                                                                            : 'text-slate-600 hover:text-violet-400'
                                                                    }`}
                                                                >
                                                                    <Globe size={10} />
                                                                    多国货源对比
                                                                    {originLoading[item.hts_code] && (
                                                                        <Loader2 size={9} className="animate-spin" />
                                                                    )}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>

                                                    {/* ── 利润分析列 ── */}
                                                    {profitMode && (
                                                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                                                            <div className="flex items-center">
                                                                <span className="text-slate-600 text-xs mr-1">$</span>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.01"
                                                                    value={skuCosts[item.sku] ?? globalCost}
                                                                    onChange={(e) => setSkuCosts((prev) => ({ ...prev, [item.sku]: e.target.value }))}
                                                                    placeholder={globalCost || '0.00'}
                                                                    className="w-16 bg-surface-2 border border-white/[0.06] hover:border-violet-500/30 focus:border-violet-500/50 rounded-md px-1.5 py-0.5 text-xs text-white text-right focus:outline-none transition-colors"
                                                                />
                                                            </div>
                                                        </td>
                                                    )}
                                                    {profitMode && (
                                                        <td className="px-3 py-3">
                                                            {profitRes ? (
                                                                <div className="text-xs space-y-0.5">
                                                                    <div className="text-slate-400">
                                                                        ${profitRes.landedCost.toFixed(2)}
                                                                        <span className="text-slate-600 ml-1 text-[10px]">含税</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-1">
                                                                        <TrendingDown size={10} className="text-violet-400 flex-shrink-0" />
                                                                        <span className="text-violet-400 font-semibold">
                                                                            ≥ ${profitRes.requiredPrice.toFixed(2)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <span className="text-xs text-slate-600">填入成本</span>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>

                                                {/* Expanded: alternatives + origin comparison */}
                                                {isOpen && (item.alts.length > 0 || originData[item.hts_code]) && (
                                                    <tr key={`${item.sku}-alts`} className="border-b border-white/[0.04]">
                                                        <td colSpan={profitMode ? 9 : 7} className="px-4 pb-3 pt-0 space-y-3">

                                                            {/* ── 备选 HTS 分类 ── */}
                                                            {item.alts.length > 0 && (
                                                                <div className={`rounded-xl p-3 border ${RISK_BG['safe']} space-y-2`}>
                                                                    <p className="text-xs font-semibold text-emerald-400">
                                                                        💡 AI 推荐备选分类（税率可能更低，建议咨询持牌报关行确认适用性）
                                                                    </p>
                                                                    {item.alts.map((alt, i) => (
                                                                        <div key={i} className="flex items-center gap-3 text-xs">
                                                                            <span className="font-mono text-brand-300 flex-shrink-0">{alt.hts_code_formatted}</span>
                                                                            <span className="text-slate-400 flex-1 min-w-0 line-clamp-1">{alt.description}</span>
                                                                            <span className="text-emerald-400 font-semibold flex-shrink-0">
                                                                                对华附加税 ≥{alt.cn_est_pct}%
                                                                            </span>
                                                                            {profitRes && (
                                                                                <span className="text-violet-400 text-[10px] flex-shrink-0">
                                                                                    → 可降到岸 ${(profitRes.unitCost * (1 + alt.cn_est_pct / 100)).toFixed(2)}
                                                                                </span>
                                                                            )}
                                                                            <a
                                                                                href={`https://hts.usitc.gov/?query=${alt.hts_code}`}
                                                                                target="_blank" rel="noreferrer"
                                                                                className="text-slate-600 hover:text-brand-400 transition-colors flex-shrink-0"
                                                                            >
                                                                                <ExternalLink size={11} />
                                                                            </a>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {/* ── 多国货源关税对比 ── */}
                                                            {originLoading[item.hts_code] && (
                                                                <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
                                                                    <Loader2 size={12} className="animate-spin text-violet-400" />
                                                                    正在加载多国税率数据...
                                                                </div>
                                                            )}
                                                            {!originLoading[item.hts_code] && originData[item.hts_code]?.length > 0 && (() => {
                                                                const origins = originData[item.hts_code];
                                                                const bestOrigin = origins.reduce((best, o) =>
                                                                    o.rate_pct < best.rate_pct ? o : best
                                                                );
                                                                const cnOrigin = origins.find((o) => o.country_code === 'CN');
                                                                const unitCost = parseFloat(skuCosts[item.sku] || globalCost || '0') || 0;

                                                                return (
                                                                    <div className="rounded-xl border border-violet-500/20 bg-violet-500/5 p-3">
                                                                        <div className="flex items-center gap-2 mb-3">
                                                                            <Globe size={13} className="text-violet-400" />
                                                                            <p className="text-xs font-semibold text-violet-300">
                                                                                多国货源关税对比（HTS {item.hts_code_formatted}）
                                                                            </p>
                                                                        </div>
                                                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                                                            {origins.map((origin) => {
                                                                                const isBest   = origin.country_code === bestOrigin.country_code;
                                                                                const isCN     = origin.country_code === 'CN';
                                                                                const saving   = cnOrigin && !isCN
                                                                                    ? cnOrigin.rate_pct - origin.rate_pct
                                                                                    : 0;
                                                                                const savedAmt = unitCost > 0 && saving > 0
                                                                                    ? unitCost * saving / 100
                                                                                    : 0;

                                                                                const riskColor = origin.risk === 'danger'
                                                                                    ? 'text-red-400'
                                                                                    : origin.risk === 'warning'
                                                                                    ? 'text-amber-400'
                                                                                    : 'text-emerald-400';
                                                                                const riskBg = origin.risk === 'danger'
                                                                                    ? 'bg-red-500/10 border-red-500/20'
                                                                                    : origin.risk === 'warning'
                                                                                    ? 'bg-amber-500/10 border-amber-500/20'
                                                                                    : isBest && !isCN
                                                                                    ? 'bg-emerald-500/10 border-emerald-500/40 ring-1 ring-emerald-500/30'
                                                                                    : 'bg-white/[0.03] border-white/[0.08]';

                                                                                return (
                                                                                    <div key={origin.country_code}
                                                                                        className={`rounded-lg border p-2 text-center relative ${riskBg}`}
                                                                                    >
                                                                                        {isBest && !isCN && (
                                                                                            <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full whitespace-nowrap font-semibold">
                                                                                                最优
                                                                                            </span>
                                                                                        )}
                                                                                        <div className="text-base">{origin.flag}</div>
                                                                                        <div className="text-[10px] text-slate-400 mt-0.5">{origin.label}</div>
                                                                                        <div className={`text-sm font-bold mt-1 ${riskColor}`}>
                                                                                            {origin.rate_pct}%
                                                                                        </div>
                                                                                        {origin.fta && (
                                                                                            <div className="text-[9px] text-emerald-400 mt-0.5">{origin.fta}</div>
                                                                                        )}
                                                                                        {saving > 0 && (
                                                                                            <div className="text-[9px] text-emerald-300 mt-0.5">
                                                                                                省 {saving.toFixed(1)}%
                                                                                                {savedAmt > 0 && ` ($${savedAmt.toFixed(2)})`}
                                                                                            </div>
                                                                                        )}
                                                                                        {unitCost > 0 && (
                                                                                            <div className="text-[9px] text-slate-500 mt-0.5">
                                                                                                到岸 ${(unitCost * (1 + origin.rate_pct / 100)).toFixed(2)}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                        <p className="text-[9px] text-slate-600 mt-2">
                                                                            VN/IN 为 MFN 税率；MX 需 USMCA 原产地证书；税率仅供参考，正式报关请咨询报关行
                                                                        </p>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Disclaimer */}
                    <div className="card border border-amber-500/20 bg-amber-500/5 text-xs text-amber-200/60 leading-relaxed">
                        ⚠️ 对华综合税率为估算值，含 2025 年行政令（145% 对华综合关税）、Section 301 附加税及芬太尼行政令。
                        消费电子等品类暂时豁免 125% 对等税（仅需缴纳 45%+）。
                        {profitMode && profitCoverage > 0 && ' 利润影响数据基于用户输入的进货成本估算，不含运费、包装及其他操作费用。'}
                        所有结果仅供参考，正式报关请咨询持牌报关行。
                    </div>
                </div>
            )}
        </div>
    );
}
